import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, BehaviorSubject } from 'rxjs';

import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js/dist/amazon-cognito-identity.min.js';

import { Subject } from 'rxjs/Subject';
import { HttpClient } from '@angular/common/http';
import { AppState } from '../app.service';
import { CONFIG } from './auth.config';
import { UserModel } from './models/user.model';
import { COUNTRIES } from '../form/input/countries/countries.data';
import { ApiService } from '../api/api.service';

export interface AuthUserAttributeInterface {
  name: string;
  value: string;
}

@Injectable()
export class AuthService {
  private userAttributeMapping = {
    firstName: 'name',
    lastName: 'family_name',
    street: 'custom:street',
    streetNumber: 'custom:number',
    postalCode: 'custom:postalCode',
    city: 'custom:city',
    country: 'custom:country',
    phone: 'custom:phone',
    screenName: 'custom:screenName',
    email: 'email',
    newsletter: 'custom:newsletter'
  };

  private prefillEmail: string = '';
  private _currentUser: UserModel;
  private _currentUserChange: Subject<UserModel> = new Subject();

  constructor(
    private http: HttpClient,
    private appState: AppState,
    private apiService: ApiService
  ) {
  }

  public get currentUser(): UserModel {
    return this._currentUser;
  }

  public get currentUserChange(): Observable<UserModel> {
    return this._currentUserChange.asObservable();
  }

  public userFactoryFromResponse(userData: any, minimumData?: boolean): UserModel {
    const user = new UserModel();

    if (minimumData) {
      user.screenName = userData.screenName;
      user.options.displayAmounts = userData.displayAmounts;
      user.options.displayName = userData.displayScreenName;
      
      if (typeof userData.screenName !== 'undefined') {
        user.id = userData.username;
      }
    } else {
      user.id = userData.username;
      user.firstName = userData.name;
      user.lastName = userData.family_name;
      user.street = userData.street;
      user.streetNumber = userData.number;
      user.postalCode = userData.postalCode;
      user.city = userData.city;
      user.country = userData.country;
      user.email = userData.email;
      user.newsletter = userData.newsletter;
      user.phone = userData.phone;
      user.screenName = userData.screenName;
      user.options.displayAmounts = userData.displayAmounts;
      user.options.displayName = userData.displayScreenName;
    }

    return user;
  }

  public login(username: string, password: string): Observable<any> {
    const subject = new Subject();

    const res = {
      username: '',
      userAttributes: {}
    };
    const authenticationData = {
      Username: username,
      Password: password,
    };

    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);
    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId, // Your user pool id here
      ClientId: CONFIG.cognito.clientId // Your client id here
    };
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const userData = {
      Username: username,
      Pool: userPool
    };
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    console.log('authenticate user called');

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        console.log('authenticate user success');

        this.getCurrentUser()
          .subscribe(
            (user) => {
              if (user !== null) {
                console.log('get current user success');

                subject.next(user);
              }
            },
            (err) => {
              console.log('get current user error');
              console.log(err);

              subject.error(err);
            }
          );
      },
      onFailure: (err) => {
        console.log('authenticate user error');
        console.log(err);

        subject.error(err);
      },
    });

    return subject.asObservable();
  }

  public getCurrentUser(): Observable<UserModel | false> {
    const subject = new BehaviorSubject<UserModel | false>(null);

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const cognitoUser = userPool.getCurrentUser();

    console.log('get current user called');

    if (cognitoUser !== null) {
      cognitoUser.getSession((err, session) => {
        if (err) {
          console.log('get session error');
          console.log(err);

          subject.error(err);
          return;
        }
        console.log('session validity: ' + session.isValid());

        // NOTE: getSession must be called to authenticate user before calling getUserAttributes
        cognitoUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr) {
            // Handle error
            console.log('error getting user attributes');
            console.log(attrErr);

            subject.error(attrErr);
          } else {
            this._currentUser = new UserModel();

            this._currentUser.id = cognitoUser.username;

            console.log('get user attributes success');
            for (const attr of attributes) {
              if (attr.Name === 'custom:displayScreenName') {
                this._currentUser.options.displayName = attr.Value;
              } else if (attr.Name === 'custom:displayAmounts') {
                this._currentUser.options.displayAmounts = attr.Value;
              } else if (attr.Name === 'custom:newsletter') {
                this._currentUser.newsletter = attr.Value === '1' ? attr.Value : null;
              } else {
                for (const key in this.userAttributeMapping) {
                  if (this.userAttributeMapping.hasOwnProperty(key) && attr.Name === this.userAttributeMapping[key]) {
                    this._currentUser[key] = attr.Value;
                  }
                }
              }
            }

            if (this._currentUser.country && this._currentUser.country.length > 4) {
              console.log('detected deprecated country field, attempting fix');

              // This is to fix a previous bug that was saving the whole country name instead of the country code
              // It will only execute if the country length is larger than 4 chars (not a ISO code) and
              // the country is found in the country list
              const foundCountry = COUNTRIES.find((country) => {
                return country.n === this._currentUser.country;
              });

              if (foundCountry) {
                console.log('found correct country');

                this._currentUser.country = foundCountry.i;

                this.updateUserAttributes([{
                  name: 'custom:country',
                  value: foundCountry.i
                }]);
              }
            }

            this._currentUser.token = session.getIdToken().getJwtToken();

            console.log('get user token success');

            this._currentUserChange.next(this._currentUser);

            subject.next(this._currentUser);
          }
        });
      });
    } else {
      subject.next(false);
    }

    return subject.asObservable();
  }

  public forgotPassword(email): Observable<any> {
    const subject = new Subject();

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool
    };
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess(result) {
        console.log('forgotPassword result');
        console.log(result);

        subject.next(result);
      },
      onFailure: (err) => {
        console.log('forgotPassword failure');
        console.log(err);
        console.log(JSON.stringify(err));

        subject.error(err);
      },
    });

    return subject.asObservable();
  }

  public resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    const subject = new Subject();

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: (result) => {
        console.log('changePassword result');
        console.log(result);

        subject.next(result);
      },
      onFailure: (err) => {
        console.log('changePassword failure');
        console.log(err);
        console.log(JSON.stringify(err));

        subject.error(err);
      }
    });

    return subject.asObservable();
  }

  public register(userData: UserModel): Observable<any> {
    const subject = new Subject();

    const tmpattr = [
      {
        Name: 'custom:country',
        Value: userData.country
      },
      {
        Name: 'custom:street',
        Value: userData.street
      },
      {
        Name: 'custom:number',
        Value: userData.streetNumber
      },
      {
        Name: 'custom:postalCode',
        Value: userData.postalCode
      },
      {
        Name: 'custom:city',
        Value: userData.city
      },
      {
        Name: 'custom:phone',
        Value: userData.phone
      },
      {
        Name: 'custom:displayScreenName',
        Value: userData.options.displayName
      },
      {
        Name: 'custom:displayAmounts',
        Value: userData.options.displayAmounts
      },
      {
        Name: 'name',
        Value: userData.firstName
      },
      {
        Name: 'family_name',
        Value: userData.lastName
      }
    ];

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    if (userData.newsletter) {
      tmpattr.push({
        Name: 'custom:newsletter',
        Value: '1'
      });
    } else {
      tmpattr.push({
        Name: 'custom:newsletter',
        Value: '0'
      });
    }

    if (userData.creditName) {
      tmpattr.push({
        Name: 'custom:screenName',
        Value: userData.creditName
      });
    }

    const attributeList = [];

    for (const attr of tmpattr) {
      attributeList.push(new AmazonCognitoIdentity.CognitoUserAttribute(attr));
    }

    console.log('signup called');

    userPool.signUp(userData.email, userData.password, attributeList, null, (err, result) => {
      if (err) {
        console.log('signup error');
        console.log(err);

        subject.error(err);

        return;
      }

      console.log('signup success');

      this.login(userData.email, userData.password)
        .subscribe(
          (loginResult) => {
            subject.next(loginResult);
          },
          (loginErr) => {
            console.log('login error');
            console.log(loginErr);

            subject.error(loginErr);
          }
        );
    });

    return subject.asObservable();
  }

  public updateCurrentUserAttributes(userData: UserModel): Observable<any> {
    const subject = new Subject();

    const tmpattr = [
      {
        Name: 'custom:country',
        Value: userData.country
      },
      {
        Name: 'custom:street',
        Value: userData.street
      },
      {
        Name: 'custom:number',
        Value: userData.streetNumber
      },
      {
        Name: 'custom:postalCode',
        Value: userData.postalCode
      },
      {
        Name: 'custom:city',
        Value: userData.city
      },
      {
        Name: 'custom:phone',
        Value: userData.phone
      },
      {
        Name: 'custom:displayScreenName',
        Value: userData.options.displayName
      },
      {
        Name: 'custom:displayAmounts',
        Value: userData.options.displayAmounts
      },
      {
        Name: 'name',
        Value: userData.firstName
      },
      {
        Name: 'family_name',
        Value: userData.lastName
      }
    ];

    if (userData.newsletter) {
      tmpattr.push({
        Name: 'custom:newsletter',
        Value: '1'
      });
    } else {
      tmpattr.push({
        Name: 'custom:newsletter',
        Value: '0'
      });
    }

    if (userData.creditName) {
      tmpattr.push({
        Name: 'custom:screenName',
        Value: userData.creditName
      });
    }

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const attributeList = [];

    for (const attr of tmpattr) {
      attributeList.push(new AmazonCognitoIdentity.CognitoUserAttribute(attr));
    }

    const cognitoUser = userPool.getCurrentUser();

    cognitoUser.getSession((err, session) => {
      if (err) {
        console.log('get session error');
        console.log(err);

        subject.error(err);
        return;
      }
      console.log('session validity: ' + session.isValid());

      cognitoUser.updateAttributes(attributeList, (updateErr, result) => {
        if (updateErr) {
          console.log('update user error');
          console.log(err);

          subject.error(err);
        } else {
          console.log('update user success');

          subject.next(userData);
          subject.complete();
        }
      });
    });

    return subject.asObservable();
  }

  public updateUserAttributes(attributes: AuthUserAttributeInterface[]): Observable<any> {
    const subject = new Subject();

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const attributeList = [];

    for (const attr of attributes) {
      attributeList.push(new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: attr.name,
        Value: attr.value
      }));
    }

    const cognitoUser = userPool.getCurrentUser();

    cognitoUser.getSession((err, session) => {
      if (err) {
        console.log('get session error');
        console.log(err);

        subject.error(err);

        return;
      }

      cognitoUser.updateAttributes(attributeList, (updateErr, result) => {
        if (updateErr) {
          console.log('update attributes error');
          console.log(err);

          subject.error(err);
        } else {
          console.log('update attributes success');

          subject.next(true);
          subject.complete();
        }
      });
    });

    return subject.asObservable();
  }

  public logout(): void {
    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const cognitoUser = userPool.getCurrentUser();

    console.log('logout');
    console.log(cognitoUser);

    if (cognitoUser != null) {
      cognitoUser.signOut();

      this._currentUser = null;

      this._currentUserChange.next(this._currentUser);
    }
  }

  public changePassword(oldPassword: string, newPassword: string): Observable<any> {
    const subject = new Subject();

    const poolData = {
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.clientId
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const cognitoUser = userPool.getCurrentUser();

    cognitoUser.getSession((err, session) => {
      if (err) {
        console.log('get session error');
        console.log(err);

        subject.error(err);
        return;
      }
      console.log('session validity: ' + session.isValid());

      cognitoUser.changePassword(oldPassword, newPassword, (updateErr, result) => {
        if (updateErr) {
          console.log('update password error');
          console.log(updateErr);

          subject.error(updateErr);
        } else {
          console.log('update password success');

          subject.next(newPassword);
          subject.complete();
        }
      });
    });

    return subject.asObservable();
  }

  public userExists(email: string): Observable<{exists: boolean}> {
    const subject = new Subject<{exists: boolean}>();

    this.apiService.post('users/check', {
      email
    })
      .subscribe(
        (response) => {
          subject.next(response);
          subject.complete();
        }
      );

    return subject.asObservable();
  }
  
  public setPrefillEmail(email: string) {
    this.prefillEmail = email;
  }
  
  public resetPrefillEmail() {
    this.prefillEmail = '';
  }
  
  public getPrefillEmail() {
    if (this.prefillEmail !== '') {
      return this.prefillEmail;
    } else {
      return false;
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/auth/auth.service.ts